import axios, {type AxiosError} from "axios";

import {endLocalSession} from "@/src/modules/auth/lib/end-local-session";
import {
  mapFatal401ToSessionEndReason,
  parseSessionFatal401,
} from "@/src/modules/auth/lib/parse-session-fatal-401";
import {useSessionEndStore} from "@/src/modules/auth/store/session-end";
import {useSessionStore} from "@/src/modules/auth/store/session";

import {captureAxiosError} from "./glitchtip";
import {handleDispatchCenterSectionDisabled} from "./dispatch-center-section-disabled";
import {toast} from "../hooks/use-toast";

type ApiErrorBody = {
  error_code?: string;
  code?: string;
  message?: string;
  data?: {reason?: string; action?: string};
};

const axiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
});

function isPublicAuthRequestUrl(url: string | undefined): boolean {
  if (!url) {
    return false;
  }
  const normalized = url.toLowerCase();
  return (
    normalized.includes("login") ||
    normalized.includes("forgot") ||
    normalized.includes("reset") ||
    normalized.includes("logout") ||
    normalized.includes("onelogin") ||
    normalized.includes("broadcasting")
  );
}

axiosInstance.interceptors.request.use((request) => {
  if (request.url?.includes("login")) return request;

  const accessToken = useSessionStore.getState().session?.access_token;

  // @ts-ignore
  request.headers["X-App-Version"] = APP_VERSION;
  if (!(request.data instanceof FormData)) {
    request.headers["Content-Type"] = request.headers["Content-Type"] ?? "application/json";
  }

  // Preserve explicit Authorization (e.g. support impersonate/status with admin backup).
  const headers = request.headers;
  const hasAuthorization =
    typeof headers?.get === "function"
      ? headers.get("Authorization")
      : (headers?.Authorization ?? headers?.authorization);

  if (accessToken && !hasAuthorization) {
    request.headers.Authorization = `Bearer ${accessToken}`;
  }

  return request;
});

axiosInstance.interceptors.response.use(
  (response) => response,
  (error: AxiosError<ApiErrorBody>) => {
    if (useSessionEndStore.getState().sessionEnding) {
      return Promise.reject(error);
    }

    const requestUrl = error.config?.url ?? "";
    const status = error.response?.status;

    if (status === 401 && !isPublicAuthRequestUrl(requestUrl)) {
      const fatal = parseSessionFatal401(error);
      if (fatal) {
        endLocalSession({
          reason: mapFatal401ToSessionEndReason(fatal),
          source: "http",
        });
      }
    }

    const body = error?.response?.data;
    const errorCode = (body?.error_code ?? body?.code) as string | undefined;
    if (errorCode === "dispatch_center_section_disabled") {
      void handleDispatchCenterSectionDisabled(body?.message);
    }
    if (errorCode === "IMPERSONATION_READ_ONLY") {
      toast.error(
        error?.response?.data?.message ??
          "Support impersonation is read-only. End impersonation to perform this action.",
      );
    }

    captureAxiosError(error);

    return Promise.reject(error);
  },
);

export default axiosInstance;
